import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Row, Col, Card, Alert, Badge, Spinner } from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAuthContext } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSignature, faEraser, faSave, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import config from '../../config';

const ContratoTemplateTab = () => {
    const { currentEntidade, setCurrentEntidade } = useEntidadeContext();
    const { loggedInUser } = useAuthContext();
    const sigPadRef = useRef(null);
    const [template, setTemplate] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingSignature, setIsSavingSignature] = useState(false);
    const [message, setMessage] = useState(null);
    const [signatureMessage, setSignatureMessage] = useState(null);

    useEffect(() => {
        if (currentEntidade?.contrato_template) {
            setTemplate(currentEntidade.contrato_template);
        }
    }, [currentEntidade]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await axios.put(`/api/entidades/${currentEntidade.id}`, { contrato_template: template });
            setCurrentEntidade({ ...currentEntidade, contrato_template: template });
            setMessage({ type: 'success', text: 'Modelo de contrato atualizado com sucesso!' });
        } catch (error) {
            setMessage({ type: 'danger', text: 'Erro ao salvar o modelo.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleClearSignature = () => {
        sigPadRef.current.clear();
    };

    const handleSaveSignature = async () => {
        if (sigPadRef.current.isEmpty()) {
            setSignatureMessage({ type: 'warning', text: 'Por favor, desenhe sua assinatura antes de salvar.' });
            return;
        }

        setIsSavingSignature(true);
        const signatureBase64 = sigPadRef.current.toDataURL('image/png');

        try {
            await axios.post(`/api/funcionarios/${loggedInUser.id}/update-signature`, {
                assinatura: signatureBase64
            });
            setSignatureMessage({ type: 'success', text: 'Sua assinatura profissional foi salva com sucesso!' });
        } catch (error) {
            setSignatureMessage({ type: 'danger', text: 'Erro ao salvar assinatura no servidor.' });
        } finally {
            setIsSavingSignature(false);
        }
    };

    const variaveis = [
        { label: 'Nome do Paciente', value: '{{paciente_nome}}' },
        { label: 'CPF do Paciente', value: '{{paciente_cpf}}' },
        { label: 'Nacionalidade', value: '{{paciente_nacionalidade}}' },
        { label: 'Estado Civil', value: '{{paciente_estado_civil}}' },
        { label: 'Profissão', value: '{{paciente_profissao}}' },
        { label: 'Endereço do Paciente', value: '{{paciente_endereco}}' },
        { label: 'Telefone do Paciente', value: '{{paciente_telefone}}' },
        { label: 'E-mail do Paciente', value: '{{paciente_email}}' },
        { label: 'Nome da Clínica', value: '{{clinica_nome}}' },
        { label: 'CNPJ da Clínica', value: '{{clinica_cnpj}}' },
        { label: 'Serviços Contratados', value: '{{servicos_contratados}}' },
        { label: 'Valor Total', value: '{{valor_total}}' },
        { label: 'Data de Hoje', value: '{{data_hoje}}' },
    ];

    return (
        <Card className="border-0 shadow-sm">
           <Card.Body>
                {message && <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}
                <Row>
                    <Col md={8}>
                        <Form.Group className="mb-3">
                            <Form.Label><strong>Texto do Contrato</strong></Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={20} 
                                value={template} 
                                onChange={(e) => setTemplate(e.target.value)}
                                placeholder="Cole aqui o texto legal do contrato..."
                            />
                        </Form.Group>
                        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Salvando...' : 'Salvar Modelo'}
                        </Button>
                    </Col>
                    <Col md={4}>
                        <h6>Variáveis Disponíveis</h6>
                        <p className="small text-muted">Copie e cole estas tags no seu texto. Elas serão substituídas automaticamente pelos dados reais.</p>
                        <div className="d-flex flex-wrap gap-2">
                            {variaveis.map(v => (
                                <Badge key={v.value} bg="info" className="p-2 cursor-pointer" onClick={() => navigator.clipboard.writeText(v.value)} title="Clique para copiar">
                                    {v.label}: {v.value}
                                </Badge>
                            ))}
                        </div>
                    </Col>
                </Row>

                <hr className="my-5" />
                
                <Row>
                    <Col md={12}>
                        <h5 className="mb-3">
                            <FontAwesomeIcon icon={faFileSignature} className="me-2 text-primary" /> 
                            Minha Assinatura Profissional
                        </h5>
                        <p className="text-muted small">Desenhe sua assinatura no quadro abaixo. Ela será utilizada automaticamente no rodapé dos contratos que você gerar.</p>
                        
                        {signatureMessage && <Alert variant={signatureMessage.type} dismissible onClose={() => setSignatureMessage(null)}>{signatureMessage.text}</Alert>}

                        <Row className="align-items-center">
                            <Col md={6}>
                                <div className="border border-2 rounded-3 bg-white mb-3" style={{ borderStyle: 'dashed' }}>
                                    <SignatureCanvas 
                                        ref={sigPadRef}
                                        penColor="black"
                                        backgroundColor="rgba(0,0,0,0)"
                                        canvasProps={{
                                            className: 'signature-canvas',
                                            style: { width: '100%', height: '200px', cursor: 'crosshair' }
                                        }}
                                    />
                                </div>
                                <div className="d-flex gap-2">
                                    <Button variant="outline-secondary" size="sm" onClick={handleClearSignature}>
                                        <FontAwesomeIcon icon={faEraser} className="me-1" /> Limpar
                                    </Button>
                                    <Button variant="success" size="sm" onClick={handleSaveSignature} disabled={isSavingSignature}>
                                        {isSavingSignature ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Salvar Minha Assinatura</>}
                                    </Button>
                                </div>
                            </Col>
                            <Col md={6} className="text-center text-muted border-start">
                                <div className="p-3">
                                    <h6>Exemplo de aplicação:</h6>
                                    <div style={{ borderBottom: '1px solid #000', width: '200px', margin: '40px auto 5px' }}></div>
                                    <div className="small"><strong>{loggedInUser?.nome}</strong></div>
                                    <div className="small">Responsável / Testemunha</div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};
export default ContratoTemplateTab;
